/**
 * Event reminders — WHEN and WHO (feedback #828, Hestia 2026-09-17).
 *
 * A reminder is:
 *   when — EITHER a preset offset before the event ("1 day before")
 *          OR a specific date + time, typed in the FIRM's timezone and sent
 *          to the API as a UTC instant (the events.start_time convention —
 *          see ./eventTime.js).
 *   who  — "Everyone who can see this event" (recipient_user_id null, the
 *          only behaviour before #828) or one named team member.
 *
 * Pure helpers only, shared by ReminderEditor (Calendar + MatterDetail event
 * forms), the Calendar chips, and GlobalReminders.  Tested under
 * TZ=Asia/Taipei so a browser-zone leak cannot hide behind a firm-zone match.
 */
import { format } from 'date-fns'
import { utcToLocalInput, localInputToUtc, toFirmWallClock } from './eventTime'

// Preset offsets, minutes before the event.  Keep every value any writer of
// event_reminders produces (discovery.js stacks 14d / 7d / 3d / 1d) so an
// existing reminder always matches a preset rather than a "custom" row.
export const REMINDER_PRESETS = [
  { minutes: 15, label: '15 minutes before' },
  { minutes: 30, label: '30 minutes before' },
  { minutes: 60, label: '1 hour before' },
  { minutes: 120, label: '2 hours before' },
  { minutes: 480, label: '8 hours before' },
  { minutes: 1440, label: '1 day before' },
  { minutes: 2880, label: '2 days before' },
  { minutes: 4320, label: '3 days before' },
  { minutes: 10080, label: '1 week before' },
  { minutes: 20160, label: '2 weeks before' },
]

export const EVERYONE_LABEL = 'Everyone who can see this event'

/** "15 minutes before", "1 day before", or a humanized custom offset. */
export function minutesLabel (m) {
  if (m === null || m === undefined || m === '') return ''
  const n = Number(m)
  if (!Number.isFinite(n) || n < 0) return ''
  if (n === 0) return 'At start time'
  const preset = REMINDER_PRESETS.find(p => p.minutes === n)
  if (preset) return preset.label
  const plural = (v, unit) => `${v} ${unit}${v === 1 ? '' : 's'} before`
  if (n % 10080 === 0) return plural(n / 10080, 'week')
  if (n % 1440 === 0) return plural(n / 1440, 'day')
  if (n % 60 === 0) return plural(n / 60, 'hour')
  return plural(n, 'minute')
}

/**
 * A server timestamp for a `timestamp without time zone` column is UTC by
 * convention, but it reaches the browser in two shapes: node-pg serialises a
 * Date ("…Z") while Postgres' own json_build_object emits no zone at all
 * ("2026-09-30T16:00:00").  `new Date()` reads the zoneless form as the
 * BROWSER's local time.  Normalise to a real UTC ISO string.
 */
export function normalizeUtc (v) {
  if (v === null || v === undefined || v === '') return null
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.toISOString()
  let s = String(v).trim()
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(s)) s = s.replace(' ', 'T') + 'Z'
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/** Blank editor row. */
export function emptyReminderRow (minutes = 60) {
  return { mode: 'offset', minutes: String(minutes), at_date: '', at_time: '', recipient_user_id: '', repeat_minutes: null }
}

/**
 * Anything that may sit in a form's `reminders` array -> an editor row.
 * Accepts: a legacy preset string/number (old form drafts in localStorage
 * still hold those), an editor row, or a reminder object as the API returns
 * it ({reminder_minutes, remind_at, recipient_user_id, repeat_minutes}).
 */
export function toReminderRow (r, tz) {
  if (r === null || r === undefined || r === '') return null
  if (typeof r === 'number' || typeof r === 'string') {
    const n = parseInt(r, 10)
    return Number.isFinite(n) && n > 0 ? emptyReminderRow(n) : null
  }
  if (typeof r !== 'object') return null
  if (r.mode === 'offset' || r.mode === 'at') {
    return { ...emptyReminderRow(), ...r }
  }
  const recipient = r.recipient_user_id != null ? String(r.recipient_user_id) : ''
  const repeat = r.repeat_minutes || null
  const at = normalizeUtc(r.remind_at)
  if (at) {
    const local = utcToLocalInput(at, tz)            // "YYYY-MM-DDTHH:mm", firm clock
    const [d, t] = local.split('T')
    return { mode: 'at', minutes: '', at_date: d || '', at_time: t || '', recipient_user_id: recipient, repeat_minutes: repeat }
  }
  const n = parseInt(r.reminder_minutes ?? r.minutes, 10)
  if (!Number.isFinite(n) || n <= 0) return null
  return { mode: 'offset', minutes: String(n), at_date: '', at_time: '', recipient_user_id: recipient, repeat_minutes: repeat }
}

export function normalizeReminderRows (list, tz) {
  if (!Array.isArray(list)) return []
  return list.map(r => toReminderRow(r, tz)).filter(Boolean)
}

/**
 * Editor rows -> the API `reminders` array.  A specific-time row with no date
 * yet is incomplete and is dropped (the editor flags it); a date with no time
 * means 9:00 AM, the start of the firm's day.
 */
export function reminderRowsToPayload (rows, tz) {
  const out = []
  for (const row of normalizeReminderRows(rows, tz)) {
    const who = row.recipient_user_id ? { recipient_user_id: Number(row.recipient_user_id) } : {}
    const rep = row.repeat_minutes ? { repeat_minutes: Number(row.repeat_minutes) } : {}
    if (row.mode === 'at') {
      if (!row.at_date) continue
      const remindAt = localInputToUtc(`${row.at_date}T${row.at_time || '09:00'}`, tz)
      if (!remindAt) continue
      out.push({ remind_at: remindAt, ...who, ...rep })
    } else {
      const n = parseInt(row.minutes, 10)
      if (!Number.isFinite(n) || n <= 0) continue
      out.push({ minutes: n, ...who, ...rep })
    }
  }
  return out
}

/** True when a specific-time row has no date yet — the editor warns about it. */
export function isIncompleteRow (row) {
  return !!row && row.mode === 'at' && !row.at_date
}

/** "Sep 30, 9:00 AM" in the firm's timezone. */
export function formatRemindAt (value, tz) {
  const iso = normalizeUtc(value)
  if (!iso) return ''
  const wall = toFirmWallClock(iso, tz)
  return wall ? format(wall, 'MMM d, h:mm a') : ''
}

/**
 * Chip text for a reminder as the API returns it:
 *   "15 minutes before · Everyone"   "Sep 30, 9:00 AM · Hestia Smith"
 * `nameFor(id)` is an optional lookup for when the row carries only an id.
 */
export function reminderChipLabel (r, { tz, nameFor } = {}) {
  if (!r) return ''
  const when = r.remind_at ? formatRemindAt(r.remind_at, tz) : minutesLabel(r.reminder_minutes ?? r.minutes)
  let who = 'Everyone'
  if (r.recipient_user_id != null && r.recipient_user_id !== '') {
    who = r.recipient_name || (nameFor && nameFor(r.recipient_user_id)) || 'One team member'
  }
  return `${when} · ${who}`
}
